import { ReactNode, useState } from 'react'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { Box, Collapse, IconButton } from '@mui/material'
import Button from '@mui/material/Button'
import { useNavigate } from '@reach/router'
import clsx from 'clsx'
import { format } from 'date-fns'
import { t } from 'i18next'
import { QueryObserverResult, useMutation, useQueryClient } from 'react-query'

import Breadcrumb from 'src/components/shared/Breadcrumb/Breadcrumb'
import PdfDialog from 'src/components/shared/PdfDialog'
import { AlertType } from 'src/components/shared/ui/AlertDialog/enums'
import ButtonPrimaryOutlined from 'src/components/shared/ui/ButtonPrimaryOutlined'
import { DeleteIcon } from 'src/components/shared/ui/Icons'
import InfoBar from 'src/components/shared/ui/InfoBar/InfoBar'
import Loader from 'src/components/shared/ui/Loader/Loader'
import * as routes from 'src/constants/routes'
import { useAlertContext } from 'src/contexts/AlertContext'
import { useTipoMensajeContext } from 'src/contexts/TipoMensajeContext'
import { TipoMensaje, TipoMensajeDescripcion } from 'src/enums/EscritosLegales'
import { EstadoMensaje, EstadoLexnetMensaje } from 'src/enums/EstadoMensaje'
import useBlob from 'src/hooks/useBlob'
import { Mensaje, Adjunto as FicheroAdjunto } from 'src/interfaces'
import { getEscrito } from 'src/services/EscritosServices'
import { deleteRequest } from 'src/services/MensajeLexonClassification'
import { GetMessageResponse } from 'src/services/MessageService'
import Adjunto from './components/Adjunto/Adjunto'
import AsignarExpedienteAlert from './components/AsignarExpedienteAlert'
import AsignarExpedienteDialog from './components/AsignarExpedienteDialog'
import Sujeto from './components/Sujeto'
import VerificacionLecturaDialog from './components/VerificacionLecturaDialog'
import useStyles from './hooks/useStyles'

type Props = {
  messageQuery: QueryObserverResult<GetMessageResponse>
}

export default function MessageView({ messageQuery }: Props): JSX.Element {
  const classes = useStyles()
  const [openedAdjuntoId, setOpenedAdjuntoId] = useState<string | null>(null)
  const [isAsignarExpedienteDialogOpened, setAsignarExpedienteDialogOpened] = useState(false)
  const queryClient = useQueryClient()
  const { CreateBlobFromUrl } = useBlob()
  const { getRoute: getEscritoRoute } = useTipoMensajeContext()
  const { addAlert } = useAlertContext()
  const navigate = useNavigate()

  function closeAsignarExpedienteDialog() {
    setAsignarExpedienteDialogOpened(false)
  }

  function openAsignarExpedienteDialog() {
    setAsignarExpedienteDialogOpened(true)
  }

  function onPdfError() {
    if (messageQuery?.data?.results?.id) {
      queryClient.invalidateQueries(['messageDetail', messageQuery.data.results.id])
    }
  }

  function showErrorDialog() {
    addAlert({
      message: t('dialog.message.remove_accion_error'),
      type: AlertType.ERROR
    })
  }

  const deleteAction = useMutation(
    (lexonClassificationId: number) => {
      const message = messageQuery?.data?.results
      if (!message) {
        return Promise.reject()
      }
      return deleteRequest(lexonClassificationId)
    },
    {
      onSuccess: () => {
        const message = messageQuery?.data?.results
        // eslint-disable-next-line
        queryClient.invalidateQueries(['messageDetail', message!.id])
        queryClient.invalidateQueries(['messagesListed'])
      },
      onError: () => {
        showErrorDialog()
      }
    }
  )

  function handleOnDeleteAction(lexonClassificationId) {
    deleteAction.mutate(lexonClassificationId)
  }

  if (!messageQuery?.data?.results?.id && messageQuery.isLoading) {
    return (
      <div className={classes.loaderWrap}>
        <Loader size="large" />
      </div>
    )
  }

  const { data } = messageQuery

  if (messageQuery.isError || !data) {
    // return <Alert severity="error">Se ha producido un error recuperando este mensaje</Alert>
    return (
      <Box sx={{ marginTop: '24px' }}>
        <InfoBar
          message="Se ha producido un error recuperando este mensaje"
          type={AlertType.ERROR}
          showCloseIcon={false}
        />
      </Box>
    )
  }

  const message = data.results

  const fechaEnvio = new Date(message.datos.fechaEnvio)

  async function initSubsanacion(message: Mensaje) {
    const messageId = Number(message.id)
    const { escrito } = await getEscrito(messageId)
    const route = getEscritoRoute(escrito.tipoMensaje)

    escrito.esCorreccion = true
    escrito.idMensajeCorreccion = messageId

    if (message.adjuntos && message.adjuntos.length) {
      message.adjuntos.forEach(async (adjunto: FicheroAdjunto) => {
        const blob = await CreateBlobFromUrl(adjunto.path)
        if (blob) {
          if (adjunto.principal) {
            escrito.documentoPrincipal.push({
              archivo: adjunto.nombre,
              descripcion: adjunto.descripcion,
              hash: '',
              subtipo: null,
              pendienteCompletar: false,
              sign: true
            })
            escrito.documentoPrincipalArchivo = new File([blob], adjunto.nombre)
          } else {
            if (!escrito.documentosAnexos) {
              escrito.documentosAnexos = []
            }
            escrito.documentosAnexos.push({
              archivo: adjunto.nombre,
              descripcion: adjunto.descripcion,
              hash: '',
              subtipo: null
            })
          }
        }
      })
    }
    navigate(routes.ESCRITOS_LEXNET(route), { state: { escrito } })
  }

  function getSubsanacionButton(message: Mensaje): ReactNode {
    if (
      message.tipoMensaje === TipoMensaje.ACUSE.toString() &&
      message.estadoLexnet === EstadoLexnetMensaje.RECHAZADO &&
      message.escritos.tipoEscrito.codigo !== TipoMensaje.MONITORIO &&
      message.fechaUltimaDescarga !== null
    ) {
      return (
        <>
          <div className={clsx(classes.sectionHeader, classes.subsanacion)}>
            <h3>Subsanación</h3>
          </div>
          <Button
            startIcon={<AddCircleOutlineIcon />}
            className={classes.editExpedientButton}
            onClick={() => {
              initSubsanacion(message)
            }}
          >
            Crear escrito de subsanación
          </Button>
        </>
      )
    }
    return <></>
  }

  function getAdjunto(tipoMensaje: string, adjunto: FicheroAdjunto): ReactNode {
    if (tipoMensaje === TipoMensaje.NOTIFICACION.toString() || adjunto.principal === true) {
      return (
        <div key={adjunto.id}>
          {adjunto.nombre.endsWith('.pdf') ? (
            <PdfDialog
              actions={[
                <ButtonPrimaryOutlined
                  onClick={() => {
                    setOpenedAdjuntoId(null)
                  }}
                >
                  {t('actions.close')}
                </ButtonPrimaryOutlined>
              ]}
              onPdfError={onPdfError}
              onClose={() => {
                setOpenedAdjuntoId(null)
              }}
              open={openedAdjuntoId === adjunto.id}
              documentUri={adjunto.path}
              filename={adjunto.nombre}
            >
              {(_open, openDialog) => (
                <a
                  href={adjunto.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={event => {
                    setOpenedAdjuntoId(adjunto.id)
                    event.preventDefault()
                    openDialog()
                  }}
                >
                  <Adjunto adjunto={adjunto} />
                </a>
              )}
            </PdfDialog>
          ) : (
            <a href={adjunto.path} target="_blank" rel="noopener noreferrer">
              <Adjunto adjunto={adjunto} />
            </a>
          )}
        </div>
      )
    }
    return <></>
  }

  return (
    <div className={classes.message}>
      <Breadcrumb section="Operativa LexNET">
        <div className={classes.buttonsContainer}>
          <ButtonPrimaryOutlined
            onClick={() => {
              navigate(-1)
            }}
          >
            Atrás
          </ButtonPrimaryOutlined>
        </div>
      </Breadcrumb>
      <Collapse
        in={
          message.estado === EstadoMensaje.INITIAL &&
          message.tipoMensaje !== TipoMensaje.ACUSE.toString()
        }
      >
        <VerificacionLecturaDialog messageId={message.id} />
      </Collapse>
      <Collapse
        in={
          message.estado === EstadoMensaje.PENDIENTE_DESCARGA &&
          message.tipoMensaje !== TipoMensaje.ACUSE.toString()
        }
      >
        <VerificacionLecturaDialog messageId={message.id} pendienteDescarga />
      </Collapse>
      <Collapse
        in={
          message.estado === EstadoMensaje.FIRMADO_EN_ORIGEN &&
          message.tipoMensaje !== TipoMensaje.ACUSE.toString()
        }
      >
        <VerificacionLecturaDialog messageId={message.id} firmadoEnOrigen />
      </Collapse>
      <Collapse in={message.estado === EstadoMensaje.DESCARGADO}>
        <AsignarExpedienteAlert
          onClickAssign={openAsignarExpedienteDialog}
          tipoMensaje={message.tipoMensaje}
        />
      </Collapse>
      <div className={classes.datos}>
        {message.escritos !== null && (
          <>
            <div className={classes.sectionHeader}>
              <h3>Tipo Mensaje</h3>
            </div>
            <div className={classes.body}>
              {TipoMensajeDescripcion[message.escritos.tipoEscrito.descripcion.replace(' ', '_')]}
            </div>
          </>
        )}
        <div className={classes.sectionHeader}>
          <h3>Asunto</h3>
        </div>
        <div className={classes.body}>{message.datos.asunto}</div>
        <div className={classes.sectionHeader}>
          <h3>Remitente</h3>
        </div>
        <div className={classes.body}>
          {message.mensajeOrigenes.map(mensajeOrigen => (
            <Sujeto key={mensajeOrigen.origen.id} sujeto={mensajeOrigen.origen} />
          ))}
        </div>
        <div className={classes.sectionHeader}>
          <h3>Destinatarios</h3>
        </div>
        <div className={classes.body}>
          {message.mensajeDestinos.map(mensajeDestino => (
            <Sujeto key={mensajeDestino.destino.nombre} sujeto={mensajeDestino.destino} />
          ))}
        </div>
        <div className={classes.sectionHeader}>
          <h3>Fecha de envío</h3>
        </div>
        <div className={classes.body}>{format(fechaEnvio, 'dd/MM/yyyy - HH:mm:ss')}</div>
        {message.tipoMensaje === TipoMensaje.NOTIFICACION.toString() &&
          (message.leido || message.fechaUltimaDescarga !== null) && (
            <>
              <div className={classes.sectionHeader}>
                <h3>Fecha de aceptación</h3>
              </div>
              <div className={classes.body}>
                {message.leido
                  ? '--/--/----'
                  : format(new Date(message.fechaUltimaDescarga), 'dd/MM/yyyy - HH:mm:ss')}
              </div>
            </>
        )}
        <div className={classes.sectionHeader}>
          <h3>Datos</h3>
        </div>
        <div className={classes.body}>
          <div>
            <div>
              <strong>Procedimiento destino: </strong>
              <span>
                {message.datos.asunto} {message.datos.numeroProcedimiento}
              </span>
            </div>
            {message.datos.numeroProcedimientoOrigen && (
              <div>
                <strong>Número de procedimiento origen </strong>
                <span>
                  {message.datos.numeroProcedimientoOrigen}
                  {message.datos.tipoProcedimientoOrigen
                    ? ` - ${message.datos.tipoProcedimientoOrigen.texto}`
                    : ''}
                </span>
              </div>
            )}
            {message.datos.esPieza && (
              <div>
                <strong>Número de pieza </strong>
                <span>{message.datos.numeroPieza}</span>
              </div>
            )}
          </div>
          <div>
            <strong>Identificador en Lexnet: </strong>
            <span>{message.id}</span>
          </div>
        </div>
        {message.adjuntos.length > 0 && (
          <>
            <div className={classes.sectionHeader}>
              <h3>Documentos</h3>
            </div>
            <div className={clsx(classes.body, classes.adjuntos)}>
              {message.adjuntos.map(adjunto => getAdjunto(message.tipoMensaje, adjunto))}
            </div>
          </>
        )}
        {message.estado === EstadoMensaje.ASIGNADO_EXPEDIENTE && (
          <div className={classes.wrapperClassification}>
            <div className={classes.sectionHeader}>
              <h3>Clasificación</h3>
            </div>
            {message.lexonClassifications.map(lexonClassification => (
              <div className={classes.lexonClassification} key={lexonClassification.id}>
                <div className={classes.lexonClassificationRow}>
                  <div>
                    <strong>Empresa: </strong>
                    {lexonClassification.companyName}
                  </div>
                  <div>
                    <strong>Expediente: </strong>
                    <span>
                      {`${lexonClassification.expedientIdOwnFile ??
                        ''} - ${lexonClassification.expedientDescription ?? ''}`}
                    </span>
                  </div>
                </div>
                <div className={classes.lexonClassificationButton}>
                  <IconButton
                    aria-label="Cerrar"
                    onClick={() => handleOnDeleteAction(lexonClassification.id)}
                    size="medium"
                  >
                    <DeleteIcon color="primary" />
                  </IconButton>
                </div>
              </div>
            ))}
            <Button
              startIcon={<AddCircleOutlineIcon />}
              className={classes.editExpedientButton}
              onClick={openAsignarExpedienteDialog}
            >
              Añadir clasificación
            </Button>
          </div>
        )}
        {getSubsanacionButton(message)}
      </div>
      <AsignarExpedienteDialog
        message={message}
        isOpened={isAsignarExpedienteDialogOpened}
        onCloseDialog={closeAsignarExpedienteDialog}
      />
    </div>
  )
}
